import React, { useMemo, useState, Fragment, useEffect, useRef } from "react";
import { createCompanyHierarchy, enrichCompanyHierarchy, getCompanyHierarchy } from "../../../apis/ext";
import { useMutation } from "react-query";
import { useStoreCRMSearch } from "../../../stores/useStoreCRMSearch";
import { useCRMSearchRecords } from "../../../apiHooks/useCRMSearchRecords";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";
import { isEqual } from "lodash";
import Image from "../../Tags/Image";
import { Button } from "../../ui/button";
import Text from "../../Tags/Text";
import EllipsisText from "../../EllipsisText";
import TooltipWrapper from "../TooltipWrapper";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import CRM from "../crm";
import { Switch } from "../../ui/switch";
import { Download, FilterIcon, Search } from "lucide-react";
import Input from "../../Tags/Input";
import LoaderInfinite from "./LoaderInfinite";
import { useDownloadHierarchyCSV } from "../../../hooks/useDownloadHierarchyCSV";

/**
 * DrilldownTable
 * - TailwindCSS-driven table with expandable rows up to 4 levels deep
 * - Root object is singular; children are nested under `children`
 *
 * Props:
 *  - data: Root node object
 *  - maxDepth?: number (default 4)
 */

const sampleData = {
  id: "root",
  name: "ING Group",
  label: "Ultimate parent",
  website: "ing.com",
  location: "Amsterdam, Netherlands",
  po: "ing.com",
  owner: "S. S. Rahman",
  type: "Prospect",
  logoUrl: "https://dummyimage.com/32x32/ff6a00/ffffff.png&text=IG", // placeholder
  children: [
    {
      id: "lambda",
      name: "LambdaTest",
      label: "Subsidiary",
      website: "lambdatest.com",
      location: "CT Amsterdam, Netherlands",
      po: "lambdatest.com",
      owner: "System user",
      type: "Prospect",
      logoUrl: "https://dummyimage.com/32x32/000000/ffffff.png&text=LT",
      children: [],
    },
    {
      id: "oyak",
      name: "Oyak",
      label: "Subsidiary",
      website: "oyak.com.tr",
      location: "Ankara, Turkey",
      po: "oyal.com.tr",
      owner: "S. S. Rahman",
      type: "Prospect",
      logoUrl: "https://dummyimage.com/32x32/f0f0f0/111111.png&text=OY",
      children: [
        {
          id: "osman",
          name: "Osman Logistics",
          label: "Acquisition",
          website: "omsan.com",
          location: "Cevizli, Turkey",
          po: null,
          owner: null,
          type: null,
          logoUrl: "https://dummyimage.com/32x32/2b8ced/ffffff.png&text=OL",
          children: [
            {
              id: "ing-au",
              name: "ING Bank (Australia)",
              label: "Subsidiary",
              website: "ing.com.au",
              location: "Sydney, Australia",
              po: "ing.au",
              owner: "S. S. Rahman",
              type: "Prospect",
              logoUrl: "https://dummyimage.com/32x32/ff6a00/ffffff.png&text=IA",
              children: [],
            },
          ],
        },
      ],
    },
  ],
};

// const getAllUniqueOrgIds = (hierarchy) => {
//   const uniqueIds = new Set();

//   function traverse(node) {
//     if (!node) return;

//     if (node.organization_id) {
//       uniqueIds.add(node.organization_id);
//     }

//     if (Array.isArray(node.children)) {
//       for (const child of node.children) {
//         traverse(child);
//       }
//     }
//   }

//   traverse(hierarchy);
//   return Array.from(uniqueIds);
// };
const getAllUniqueOrgIds = (hierarchy) => {
  const uniqueIds = new Set();

  function traverse(node) {
    if (!node) return;

    const isEnriched = node?.hierarchy_metadata?.is_level_enriched ?? false;

    if (isEnriched) {
      // Add this node
      if (node?.organization_id) uniqueIds.add(node?.organization_id);

      // Add direct children
      if (Array.isArray(node.children)) {
        node.children.forEach((child) => {
          if (child.organization_id) uniqueIds.add(child.organization_id);

          // ✅ Only recurse into child if child itself is enriched
          const childIsEnriched = child?.hierarchy_metadata?.is_level_enriched ?? false;
          if (childIsEnriched) {
            traverse(child);
          }
        });
      }
    } else {
      // Traverse deeper to find any enriched descendants
      if (Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    }
  }

  traverse(hierarchy);
  return Array.from(uniqueIds);
};

const getInitials = (name = "") => {
  if (!name) return "";

  const words = name.trim().split(/\s+/); // split by spaces
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  return (words[0][0] + words[1][0]).toUpperCase();
};

const Badge = ({ children, tone = "slate" }) => (
  <span
    className={`whitespace-pre inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize
      ${tone === "green" && "bg-emerald-50 text-emerald-700 ring-emerald-200"}
      ${tone === "indigo" && "bg-indigo-50 text-indigo-700 ring-indigo-200"}
      ${tone === "slate" && "bg-slate-100 text-slate-700 ring-slate-200"}
      ${tone === "rose" && "bg-rose-50 text-rose-700 ring-rose-200"}
    `}
  >
    {children}
  </span>
);

function Chevron({ open }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-4 w-4 transition-transform ${open ? "rotate-90" : "rotate-0"}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M7 5l6 5-6 5" />
    </svg>
  );
}

function CompanyLogo({ logo, name }) {
  const [imgError, setImgError] = useState(false);

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return imgError || !logo ? (
    <span className="h-8 w-8 inline-flex items-center justify-center bg-brand-100 border-brand-200 shadow rounded-full font-bold text-[14px]">
      {getInitials(name)}
    </span>
  ) : (
    <img
      src={logo}
      alt={name}
      onError={() => setImgError(true)}
      className="mt-0.5 h-8 w-8 rounded-xl shadow object-contain no-invert"
    />
  );
}

const CompanyCell = ({ node, depth, hasChildren, open, onToggle, handleEnrichCompanyHierarchy }) => {
  return (
    <div className="flex items-start gap-2 relative">
      {depth > 0 && (
        <div className="absolute h-[1px] bg-gray-300 top-4 w-9" style={{ left: `${depth * 20}px` }}></div>
      )}
      {/* Indentation spacer */}
      <div style={{ width: depth * 20 }} />

      {/* Toggle */}
      {hasChildren ? (
        <button
          type="button"
          className="mt-1 rounded-md p-1 text-slate-500 dark:text-white hover:bg-slate-100 z-10 bg-background/70 dark:bg-gray-300"
          aria-label={open ? "Collapse" : "Expand"}
          onClick={() => {
            onToggle();

            if (!node?.hierarchy_metadata?.is_level_enriched) {
              handleEnrichCompanyHierarchy(node?.organization_id);
            }
          }}
        >
          <Chevron open={open} />
        </button>
      ) : (
        <div className="mt-1 h-6 w-6" />
      )}

      <CompanyLogo logo={node?.logo} name={node?.name} />

      {/* Meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-slate-900 dark:text-foreground">{node?.name}</span>
          {node?.hierarchy_metadata?.is_ultimate_parent ? (
            <Badge tone={"indigo"}>Ultimate Parent</Badge>
          ) : (
            <>
              {node?.relationship_data?.relationship_type && (
                <Badge
                  tone={node?.relationship_data?.relationship_type === "acquisition" ? "green" : "indigo"}
                >
                  {node?.relationship_data?.relationship_type}
                </Badge>
              )}
            </>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
          {node?.domain && (
            <a
              href={`https://${node?.domain}`}
              className="truncate underline-offset-2 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {node?.domain}
            </a>
          )}
          {node?.linkedin_url && (
            <a href={node?.linkedin_url} target="_blank">
              <Image src="/images/icons/linkedin-icon.svg" className="no-invert" />
            </a>
          )}
          {node.headquarters && (
            <span>
              {node?.domain &&
                (node.headquarters?.city || node.headquarters?.state || node.headquarters?.country) &&
                "•"}{" "}
              {node.headquarters?.city} {node.headquarters?.state && `, ${node.headquarters?.state}`}
              {node.headquarters?.country && `, ${node.headquarters?.country}`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const formatAddress = (locationData) => {
  if (locationData) {
    const { city, state, country } = locationData;

    const addressParts = [city, state, country].filter((part) => part);

    if (addressParts.length === 0) {
      return "-";
    }
    return addressParts.join(", ");
  } else {
    return "-";
  }
};

function Row({
  node,
  depth,
  maxDepth,
  openSet,
  setOpenSet,
  organizationId,
  crmInfo,
  handleEnrichCompanyHierarchy,
}) {
  const { activeCRMIntegration } = useStoreIntegrations();
  const {
    setCRMBulkSearchPayload,
    isCRMBulkSearchLoading,
    isCRMBulkSearchFetched,
    getCRMSearchRecordById,
    getCRMSearchRecordByIdFlat,
  } = useStoreCRMSearch();
  const companyCrmRecords = getCRMSearchRecordById(node?.organization_id);
  const crmRecords = getCRMSearchRecordByIdFlat(node?.organization_id);
  const id = node?.organization_id;
  const hasChildren = Array.isArray(node?.children) && node?.children.length > 0;

  const open = openSet.has(id);
  const toggle = () => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // const open = openSet[node.parentId || "root"] === id;
  // const toggle = () => {
  //   setOpenSet((prev) => {
  //     const next = { ...prev };
  //     if (open) {
  //       delete next[node.parentId || "root"];
  //     } else {
  //       next[node.parentId || "root"] = id;
  //     }
  //     return next;
  //   });
  // };

  const canDrill = depth < maxDepth - 1; // depth starts at 0

  console.log("crmRecords in row", crmRecords);
  console.log("hasCrmRecords", companyCrmRecords);

  return (
    <Fragment>
      {/* Always render the first row */}
      <tr
        className={`relative group border-b ${
          id === organizationId
            ? "bg-brand-50 border-brand-200/20 dark:bg-gray-100"
            : "bg-background border-slate-100 dark:border-border hover:bg-gray-50"
        }`}
      >
        {/* Company cell with rowspan */}
        <td
          className={`px-2 py-3 align-middle  relative ${activeCRMIntegration?.id ? "border-r" : ""}`}
          rowSpan={crmRecords?.length || 1}
        >
          {Array.from({ length: depth }).map((_, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 w-px bg-gray-300"
              style={{ left: `${i * 20 + 30}px` }} // Adjust +10 for padding if needed
            />
          ))}
          <CompanyCell
            node={node}
            depth={depth}
            hasChildren={hasChildren && canDrill}
            open={open}
            onToggle={toggle}
            handleEnrichCompanyHierarchy={handleEnrichCompanyHierarchy}
          />
        </td>

        {/* First CRM row, or fallback placeholder */}
        {crmRecords?.[0] ? (
          <>
            <td className="px-2 py-3 align-middle text-center">{crmRecords[0].crm_owner_name}</td>
            <td className="px-2 py-3 align-middle text-center">
              <a href={crmRecords[0].record_url} target="_blank">
                <EllipsisText charLimit={30}>{crmRecords[0].record_url}</EllipsisText>
              </a>
            </td>
            <td className="px-2 py-3 align-middle text-center">{crmRecords[0].data?.domain?.value || "—"}</td>
            <td className="px-2 py-3 align-middle text-center">
              {formatAddress({
                city: crmRecords[0].data?.headquarters_city?.value,
                state: crmRecords[0].data?.headquarters_state?.value,
                country: crmRecords[0].data?.headquarters_country?.value,
              }) || "—"}
            </td>
            <td className="px-2 py-3 align-middle text-center">
              {crmRecords[0].data?.employee_count?.value || "—"}
            </td>
          </>
        ) : (
          // Placeholder row content until data is available
          <>
            <td className="px-2 py-3" colSpan={5}>
              <div className="pl-9">
                {typeof companyCrmRecords === "undefined" && activeCRMIntegration?.id ? (
                  <span className="text-left text-slate-400 italic">Loading CRM info...</span>
                ) : (
                  // <CRM {...crmInfo} />
                  <>{activeCRMIntegration?.id ? "—" : ""}</>
                )}
              </div>
            </td>
          </>
        )}
      </tr>

      {/* Render remaining CRM rows if any */}
      {crmRecords?.slice(1).map((crmRecord, index) => (
        <tr
          key={`${id}-${index + 1}`}
          className={`group border-b ${
            id === organizationId
              ? "bg-brand-50 border-brand-200/20"
              : "bg-background border-slate-100 hover:bg-gray-50"
          }`}
        >
          <td className="px-2 py-3 align-middle text-center">{crmRecord.crm_owner_name}</td>
          <td className="px-2 py-3 align-middle text-center">
            <a href={crmRecord.record_url} target="_blank">
              <EllipsisText charLimit={30}>{crmRecord.record_url}</EllipsisText>
            </a>
          </td>
          <td className="px-2 py-3 align-middle text-center">{crmRecord.data?.domain?.value || "—"}</td>
          <td className="px-2 py-3 align-middle text-center">
            {formatAddress({
              city: crmRecord.data?.headquarters_city?.value,
              state: crmRecord.data?.headquarters_state?.value,
              country: crmRecord.data?.headquarters_country?.value,
            }) || "—"}
          </td>
          <td className="px-2 py-3 align-middle text-center">
            {crmRecord.data?.employee_count?.value || "—"}
          </td>
        </tr>
      ))}

      {/* Render children */}
      {hasChildren &&
        canDrill &&
        open &&
        node.children.map((child) => (
          <Row
            key={child.organization_id}
            node={child}
            depth={depth + 1}
            maxDepth={maxDepth}
            openSet={openSet}
            setOpenSet={setOpenSet}
            organizationId={organizationId}
            crmInfo={{
              recordData: { id: child?.organization_id, ...child },
              ...crmInfo,
            }}
            handleEnrichCompanyHierarchy={handleEnrichCompanyHierarchy}
          />
        ))}
    </Fragment>
  );
}

export default function Hierarchy({ maxDepth = 4, workspaceId, organizationId, crmInfo = {} }) {
  const { downloadHierarchyCSV } = useDownloadHierarchyCSV();
  const { installedCRMIntegrations, activeCRMIntegration, activeDataProviderIntegrations } =
    useStoreIntegrations();
  const { isCRMBulkSearchLoading, getCRMSearchRecordById, getCRMSearchRecordByIdFlat, crmBulkSearchData } =
    useStoreCRMSearch();

  const [totalSearchPayload, setTotalSearchPayload] = useState([]);

  const { mutate: handleCRMSearch, isLoading: isCRMSearchLoading } = useCRMSearchRecords({
    onStream: (data, variables) => {
      console.log("data?.data handleCRMSearch in hierarchy", data?.data);
    },
  });

  const handleSearchCRMRecords = (payload) => {
    console.log("inside handleSearchCRMRecords");
    if (isCRMSearchLoading) {
      return;
    }
    setTotalSearchPayload((prev) => {
      const combined = [...prev, ...payload];
      const seen = new Set();
      return combined.filter((item) => {
        if (seen.has(item.organization_id)) return false;
        seen.add(item.organization_id);
        return true;
      });
    });

    handleCRMSearch({
      data: payload,
      configId: activeCRMIntegration?.id,

      options: {
        headers: { "X-Workspace-Id": workspaceId },
      },
    });
  };

  const [isOrganizationEnrichmentPending, setIsOrganizationEnrichmentPending] = useState(null);
  const [corporateHierarchyDataNotAvailable, setCorporateHierarchyDataNotAvailable] = useState(null);
  const [hasNoCorporateHierarchyEnrichment, setHasNoCorporateHierarchyEnrichment] = useState(null);
  const [isCorporateHierarchyEnrichmentPending, setIsCorporateHierarchyEnrichmentPending] = useState(null);
  const [data, setData] = useState({});
  const [orgIds, setOrgIds] = useState([]);

  const getCompanyHierarchyFn = useMutation("get_company_hierarchy", getCompanyHierarchy, {
    onSuccess(data, variables, context) {
      console.log("getCompanyHierarchyFn data", data);
      setData(data?.data?.hierarchy);
      if (data?.data?.hierarchy) {
        setIsCorporateHierarchyEnrichmentPending(false);
      }
      if (data?.corporateHierarchyDataNotAvailable) {
        setIsCorporateHierarchyEnrichmentPending(false);
      }
      setHasNoCorporateHierarchyEnrichment(data?.hasNoCorporateHierarchyEnrichment);
      setCorporateHierarchyDataNotAvailable(data?.corporateHierarchyDataNotAvailable);
      setIsOrganizationEnrichmentPending(data?.isOrganizationEnrichmentPending);

      const allOrgIds = getAllUniqueOrgIds(data?.data?.hierarchy);

      console.log("org ids", allOrgIds);

      const filteredOrgIds = allOrgIds.filter(
        (id) => !totalSearchPayload.some((item) => String(item.organization_id).trim() === String(id).trim())
      );

      console.log("filteredOrgIds", filteredOrgIds);

      setOrgIds(filteredOrgIds);
    },
    onError(err) {
      console.log("getCompanyHierarchyFn err", err);
    },
  });

  const handleGetCompanyHierarchy = () => {
    if (getCompanyHierarchyFn.isLoading) {
      return;
    }
    if (workspaceId && organizationId) {
      getCompanyHierarchyFn.mutate({
        organization_id: organizationId,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });
    }
  };

  const prevPayloadRef = useRef(null);

  useEffect(() => {
    if (orgIds.length > 0 && activeCRMIntegration?.id) {
      const constructPayload = orgIds.map((id) => ({
        organization_id: id,
        type: "company",
      }));

      const isSame = isEqual(prevPayloadRef.current, constructPayload);
      if (!isSame && !isOrganizationEnrichmentPending) {
        prevPayloadRef.current = constructPayload;
        handleSearchCRMRecords(constructPayload);
      }
    }
  }, [orgIds, activeCRMIntegration?.id, isOrganizationEnrichmentPending]);

  // const [openSet, setOpenSet] = useState({ root: null }); // root open by default

  const [openSet, setOpenSet] = useState(() => new Set([data.organization_id]));

  const columns = useMemo(() => {
    const baseColumns = [{ key: "company", title: "Company", className: "w-[40%]" }];

    if (!activeCRMIntegration?.id) return baseColumns;

    const logoSrc = `/images/logos/integrations/${activeCRMIntegration.integration.key}.svg`;

    return [
      ...baseColumns,
      {
        key: "crm_owner_name",
        icon: <Image src={logoSrc} width={24} className="no-invert" />,
        title: "Record Owner",
        className: "w-[11%]",
      },
      {
        key: "crm_record_link",
        icon: <Image src={logoSrc} width={24} className="no-invert" />,
        title: `${activeCRMIntegration.integration.display_name} Record Link`,
        className: "w-[20%]",
      },
      {
        key: "domain",
        icon: <Image src={logoSrc} width={24} className="no-invert" />,
        title: "Domain",
        className: "w-[10%]",
      },
      {
        key: "location",
        icon: <Image src={logoSrc} width={24} className="no-invert" />,
        title: "Location",
        className: "w-[10%]",
      },
      {
        key: "employee_count",
        icon: <Image src={logoSrc} width={24} className="no-invert" />,
        title: "Employees",
        className: "w-[9%]",
      },
    ];
  }, [activeCRMIntegration?.id]);

  // enrich company heirarch

  const [isOnceEnriched, setIsOnceEnriched] = useState(false);
  const enrichCompanyHierarchyFn = useMutation("create_company_hierarchy", enrichCompanyHierarchy, {
    onSuccess(resdata, variables, context) {
      setIsOnceEnriched(true);
      setTimeout(() => {
        handleGetCompanyHierarchy();
      }, 2000);
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleEnrichCompanyHierarchy = (orgId) => {
    let payload = {
      organization_id: orgId,
    };

    enrichCompanyHierarchyFn.mutate({
      data: [{ ...payload }],
      options: {
        headers: { "X-Workspace-Id": workspaceId },
      },
    });
  };

  const createCompanyHierarchyFn = useMutation("create_company_hierarchy", createCompanyHierarchy, {
    onSuccess(resdata, variables, context) {
      console.log(resdata);
      // handleGetCompanyHierarchy();
      setIsCorporateHierarchyEnrichmentPending(true);
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleCreateCompanyHierarchy = (orgId) => {
    let payload = {
      organization_id: orgId,
    };

    createCompanyHierarchyFn.mutate({
      data: [{ ...payload }],
      options: {
        headers: { "X-Workspace-Id": workspaceId },
      },
    });
  };

  useEffect(() => {
    handleGetCompanyHierarchy();
  }, []);

  useEffect(() => {
    const doPolling = () => {
      if (isCorporateHierarchyEnrichmentPending) {
        handleGetCompanyHierarchy();
      }
    };

    const intervalId = setInterval(doPolling, 3000);

    if (!isCorporateHierarchyEnrichmentPending) {
      clearInterval(intervalId);
    }
    return () => clearInterval(intervalId);
  }, [isCorporateHierarchyEnrichmentPending]);

  useEffect(() => {
    if (!isOrganizationEnrichmentPending) {
      return; // Don't start polling if it's not pending
    }

    setIsOnceEnriched(true);

    const intervalId = setInterval(() => {
      handleGetCompanyHierarchy();
    }, 5000);

    if (!isOrganizationEnrichmentPending) {
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId); // Cleanup on unmount or dependency change
  }, [isOrganizationEnrichmentPending]);

  // handle filter

  const uniqueRelationshipTypes = useMemo(() => {
    const types = new Set();

    const traverse = (node) => {
      if (node.relationship_data?.relationship_type) {
        types.add(node.relationship_data.relationship_type);
      }

      if (Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    };

    if (Array.isArray(data)) {
      data.forEach(traverse);
    } else if (data) {
      traverse(data);
    }

    return Array.from(types).sort();
  }, [data]);

  function enrichTreeWithCRM(tree, crmRecordMap) {
    if (!tree) return null;

    const hasCRM = getCRMSearchRecordByIdFlat(tree.organization_id)?.length > 0;
    const owners = hasCRM
      ? getCRMSearchRecordByIdFlat(tree.organization_id)
          .map((r) => r.crm_owner_name)
          .filter(Boolean)
      : [];

    const updatedNode = {
      ...tree,
      crmInfo: {
        found: hasCRM,
        recordOwners: [...new Set(owners)],
      },
      children: tree.children?.map((child) => enrichTreeWithCRM(child, crmRecordMap)) || [],
    };

    return updatedNode;
  }

  const crmRecordsMap = useMemo(() => {
    const map = {};
    crmBulkSearchData.forEach((rec) => {
      const id = rec.organization_id;
      if (!map[id]) map[id] = [];
      map[id].push(rec);
    });
    return map;
  }, [crmBulkSearchData]);

  useEffect(() => {
    if (!data || !crmRecordsMap) return;

    const enriched = enrichTreeWithCRM(data, crmRecordsMap);
    setData(enriched);
  }, [crmRecordsMap]);

  function getAllUniqueRecordOwners(tree) {
    const owners = new Set();

    function traverse(node) {
      if (node.crmInfo?.recordOwners?.length) {
        node.crmInfo.recordOwners.forEach((owner) => owners.add(owner));
      }
      node.children?.forEach(traverse);
    }

    traverse(tree);
    return Array.from(owners).sort(); // Sorted alphabetically
  }

  const uniqueOwners = useMemo(() => {
    if (!data) return [];
    return getAllUniqueRecordOwners(data);
  }, [data]);

  const [filterCRMFound, setFilterCRMFound] = useState(false);
  const [filterOwner, setFilterOwner] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRelationshipType, setSelectedRelationshipType] = useState("");

  // function filterTree(node) {
  //   if (!node) return null;

  //   const matchesOwner = !filterOwner || node.crmInfo?.recordOwners?.includes(filterOwner);
  //   const matchesCRM = !filterCRMFound || node.crmInfo?.found === true;

  //   const filteredChildren = (node.children || []).map(filterTree).filter(Boolean); // remove nulls

  //   if ((matchesOwner && matchesCRM) || filteredChildren.length > 0) {
  //     return {
  //       ...node,
  //       children: filteredChildren,
  //     };
  //   }

  //   return null;
  // }

  function filterTree(node) {
    if (!node) return null;

    const lowerQuery = searchQuery.toLowerCase();

    const matchesSearch = (childNode) => {
      return (
        !searchQuery ||
        childNode.name?.toLowerCase().includes(lowerQuery) ||
        childNode.domain?.toLowerCase().includes(lowerQuery) ||
        childNode.data?.domain?.value?.toLowerCase().includes(lowerQuery)
      );
    };

    const matchesOwner = (childNode) =>
      !filterOwner || childNode.crmInfo?.recordOwners?.includes(filterOwner);

    const matchesCRM = (childNode) => !filterCRMFound || childNode.crmInfo?.found === true;

    const matchesRelationship = (childNode) => {
      if (!selectedRelationshipType) return true;
      return childNode.relationship_data?.relationship_type === selectedRelationshipType;
    };

    const shouldInclude = (childNode) =>
      matchesSearch(childNode) &&
      matchesOwner(childNode) &&
      matchesCRM(childNode) &&
      matchesRelationship(childNode);

    const filteredChildren = (node.children || []).map(filterTree).filter(Boolean).filter(shouldInclude);

    return {
      ...node,
      children: filteredChildren,
    };
  }

  const filteredData = useMemo(() => {
    return data ? filterTree(data) : null;
  }, [data, filterOwner, filterCRMFound, searchQuery, selectedRelationshipType]);

  if ((getCompanyHierarchyFn.isLoading || isCorporateHierarchyEnrichmentPending) && !isOnceEnriched) {
    return <LoaderInfinite description="Our backend systems are working on this data." />;
  }

  if (!data && hasNoCorporateHierarchyEnrichment) {
    return (
      <NoData
        className="max-w-[600px] px-10 py-10"
        icon={<Image src="/images/icons/untitledui/hierarchy.svg" className="inline-block no-invert" />}
        heading="Corporate hierarchy"
        description="Fetch all the companies in the corporate hierarchy to find new leads, overlay CRM information or resolve any conflicts."
      >
        <Button
          disabled={createCompanyHierarchyFn.isLoading}
          onClick={() => {
            handleCreateCompanyHierarchy(organizationId);
          }}
        >
          View hierarchy{" "}
          {createCompanyHierarchyFn.isLoading && <div className="loader loader_circle ml-2"></div>}
        </Button>
      </NoData>
    );
  }

  if (!data && corporateHierarchyDataNotAvailable) {
    return (
      <NoData
        className="max-w-[600px] px-10 py-10"
        icon={<Image src="/images/icons/untitledui/hierarchy.svg" className="inline-block no-invert" />}
        heading="Corporate hierarchy"
        description="No data available."
      ></NoData>
    );
  }
  if (
    !data &&
    !hasNoCorporateHierarchyEnrichment &&
    !isCorporateHierarchyEnrichmentPending &&
    !isOrganizationEnrichmentPending
  ) {
    return (
      <NoData
        className="max-w-[600px] px-10 py-10"
        icon={<Image src="/images/icons/untitledui/hierarchy.svg" className="inline-block no-invert" />}
        heading="Corporate hierarchy"
        description="No data available."
      ></NoData>
    );
  }

  const showCRMFilters = uniqueOwners && uniqueOwners.length > 0;

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => downloadHierarchyCSV(data)} variant="purple_outline">
          <Download />
          <span className="ml-2">Download Hierarchy CSV</span>
        </Button>
      </div>
      <div className="flex items-center gap-3 mb-4 bg-background border border-border rounded-lg p-4">
        <span className="font-bold text-gray-500 inline-flex space-x-1 items-center">
          <FilterIcon width={18} height={18} />:{/* <span>Filters:</span> */}
        </span>
        {showCRMFilters && (
          <label className="flex items-center gap-2 text-sm">
            Found in CRM{" "}
            <Switch
              checked={filterCRMFound ? true : false}
              onCheckedChange={(checked) => {
                setFilterCRMFound(checked);
              }}
            />
          </label>
        )}

        <Input
          icon={<Search width={18} height={18} className="text-gray-400" />}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search Company or domain"
          className="h-9 text-sm min-w-[240px]"
        />

        {showCRMFilters && (
          <select
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
            className="h-9 px-4 py-0 text-sm w-auto rounded-[--radius] border border-input bg-background text-foreground"
          >
            <option value="">All Owners</option>
            {uniqueOwners.map((owner) => (
              <option key={owner} value={owner}>
                {owner}
              </option>
            ))}
          </select>
        )}
        {uniqueRelationshipTypes && uniqueRelationshipTypes.length > 0 && (
          <select
            className="h-9 px-4 py-0 text-sm w-auto capitalize rounded-[--radius] border border-input bg-background text-foreground"
            value={selectedRelationshipType}
            onChange={(e) => setSelectedRelationshipType(e.target.value)}
          >
            <option value="">All Relationship</option>
            {uniqueRelationshipTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="w-full overflow-auto">
        <div className="min-w-[1600px] w-full overflow-x-auto rounded-2xl border border-slate-200 dark:border-border bg-background shadow-sm">
          <table className="min-w-full table-fixed border-collapse hierarchy__table">
            <thead>
              <tr className="border-b border-slate-200 dark:border-border bg-slate-50/60 dark:bg-background text-left text-sm font-semibold text-slate-600 dark:text-white">
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-3 ${c.className ?? ""}`}>
                    <div className="inline-flex items-center space-x-2">
                      {c.icon && c.icon} <span>{c.title}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row
                // node={{ ...data, parentId: "root" }}
                node={filteredData}
                depth={0}
                maxDepth={maxDepth}
                openSet={openSet}
                setOpenSet={setOpenSet}
                organizationId={organizationId}
                crmInfo={{
                  recordData: { id: data?.organization_id, ...data },
                  ...crmInfo,
                }}
                handleEnrichCompanyHierarchy={handleEnrichCompanyHierarchy}
              />
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/**
 * Usage:
 * <DrilldownTable data={yourRootNode} maxDepth={4} />
 *
 * Data shape (root object only; children nested up to 4 levels):
 * {
 *   id: string,
 *   name: string,
 *   label?: "Ultimate parent" | "Subsidiary" | "Acquisition" | string,
 *   website?: string,
 *   location?: string,
 *   po?: string | null,
 *   owner?: string | null,
 *   type?: string | null,
 *   logoUrl?: string,
 *   children?: Node[]
 * }
 */
